/**
 * @author: lv.chen3
 * @desc: 环比指标SortBy组件
 * @date: 2025/7/5
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import SortBy from '@intelligence/v3/components/TableComponents/SortBy';
import { Bubble } from '@home/components/TeaComponents';
import { generateDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import { isEmpty } from '@intelligence/v3/utils/common';
// import { isEmpty, getSanitizeHTMLString } from '@intelligence/v3/utils/common';
import {
  getDataSourceName,
  getDataSourceIconInComp,
  getMetricName,
  formatOptions,
  formatSorts,
  parseSorts,
} from './utils';
import { SortMode } from '@home/enums/common';

import type { BubbleProps } from 'tea-component';
import type { ISortByCommonConfigs } from '@intelligence/v3/components/TableComponents/SortBy';

import './index.scss';

export interface IRatioMetricSortByProps<T = any> extends IBaseProps, ISortByCommonConfigs<T> {
  dataSource: string;
  dataSourceName?: string;
  dataSourceIcon?: string;
  metric: string;
  metricName?: string;
  metricTips?: string | React.ReactNode;
  bubblePlacement?: BubbleProps['placement'];
  /**
   * 是否开启环比排序，默认值为true
   */
  useRatioSort?: boolean;
  sorts?: IRatioMetricSortState<T>[] | null;
  onSortsChange?: (sorts: IRatioMetricSortState<T>[]) => void;
}

const getMetricTips = (dataSource: string, metric: string, metricTips?: string | React.ReactNode) => {
  if (!isEmpty(metricTips)) return metricTips!;
  return null;
};

export default function RatioMetricSortBy<T = any>(props: IRatioMetricSortByProps<T>) {
  const {
    style,
    className,
    dataSource,
    dataSourceName: dataSourceNameProps,
    dataSourceIcon: dataSourceIconProps,
    metric,
    metricName: metricNameProps,
    metricTips: metricTipsProps,
    bubblePlacement = 'top',
    useRatioSort = true,
    sorts: ratioMetricSorts,
    onSortsChange: onRatioMetricSortsChange,
    ...restProps
  } = props;

  const { locale } = useIntl();

  const ratioMetricSort = ratioMetricSorts?.find((item) => {
    const dataSourceItem = item?.dataSource;
    const metricItem = item?.metric;
    return dataSource === dataSourceItem && metric === metricItem;
  });

  const _dataSourceName = getDataSourceName(locale, dataSource, dataSourceNameProps);
  const dataSourceIcon = getDataSourceIconInComp(dataSource, dataSourceIconProps);
  const metricName = getMetricName(locale, dataSource, metric, metricNameProps);
  const metricTips = getMetricTips(dataSource, metric, metricTipsProps) ?? '';

  const field = generateDataSourceMetricKey(dataSource, metric);
  const options = formatOptions(metricName, useRatioSort);
  const sorts = formatSorts<T>(ratioMetricSorts);

  return (
    <SortBy
      {...restProps}
      style={style}
      className={classnames('dbi-ratio-metric-sort-by', className, { 'has-tips': !!metricTips })}
      field={field}
      title={
        <Bubble
          style={{ maxWidth: 500 }}
          dark
          arrowPointAtCenter
          placement={bubblePlacement}
          content={<span style={{ whiteSpace: 'pre-line' }}>{metricTips}</span>}
          // content={<span style={{ whiteSpace: 'pre-line' }}>{getSanitizeHTMLString(metricTips)}</span>}
        >
          <div className="dbi-ratio-metric-sort-by__title">
            <img className="dbi-ratio-metric-sort-by__title-icon" src={dataSourceIcon} alt="" />
            <span>{metricName}</span>
            {ratioMetricSort?.mode === SortMode.Ratio && <span>(%)</span>}
          </div>
        </Bubble>
      }
      options={options}
      sorts={sorts}
      onSortsChange={(sorts) => {
        const ratioMetricSorts = parseSorts<T>(sorts);
        onRatioMetricSortsChange?.(ratioMetricSorts);
      }}
    />
  );
}
